import {makeEnumTypeGuard} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {PrecogModel, PrecogTypesEnum} from '../../../models/api/precogModel';
import {AppAction} from '../../appActions';

/*
 * Constants.
 */

type PrecogExpirationDelays = {[T in PrecogTypesEnum]: number};
export const precogExpirationDelays: PrecogExpirationDelays = {
  [PrecogTypesEnum.COMMENT]: 10000,
  [PrecogTypesEnum.MESSAGE]: 180000,
};

/*
 * Action types.
 */

export enum ConversationPrecogActionsEnum {
  /** Only one precog of one conversation */
  CONVERSATION_PRECOG_UPDATED = '@@data/precogs/CONVERSATION_PRECOG_UPDATED',
  CONVERSATION_PRECOG_DELETED = '@@data/precogs/CONVERSATION_PRECOG_DELETED',

  /** All the precogs of one conversation */
  CONVERSATION_PRECOGS_UPDATED = '@@data/precogs/CONVERSATION_PRECOGS_UPDATED',
}

/*
 * Types.
 */

export interface ConversationPrecogId {
  conversationId: number;
  teammateId: number;
}

export interface ConversationPrecogIdWithType extends ConversationPrecogId {
  precogType?: PrecogTypesEnum;
}

export interface ConversationPrecogUpdatedAction extends ActionWithPayload<PrecogModel> {
  type: ConversationPrecogActionsEnum.CONVERSATION_PRECOG_UPDATED;
}

export interface ConversationPrecogDeletedAction extends ActionWithPayload<ConversationPrecogIdWithType> {
  type: ConversationPrecogActionsEnum.CONVERSATION_PRECOG_DELETED;
}

export interface ConversationPrecogs {
  conversationId: number;
  precogs: ReadonlyArray<PrecogModel>;
  sentPrecogs: ReadonlyArray<PrecogModel> | undefined;
}

export interface ConversationPrecogsUpdatedAction extends ActionWithPayload<ConversationPrecogs> {
  type: ConversationPrecogActionsEnum.CONVERSATION_PRECOGS_UPDATED;
}

/*
 * Action creators.
 */

export function updateConversationPrecogs(
  conversationId: number,
  precogs: ReadonlyArray<PrecogModel>,
  sentPrecogs: ReadonlyArray<PrecogModel> | undefined,
): AppAction {
  return {
    type: ConversationPrecogActionsEnum.CONVERSATION_PRECOGS_UPDATED,
    payload: {conversationId, precogs, sentPrecogs},
  };
}

export function updateConversationPrecog(precog: PrecogModel): AppAction {
  return {
    type: ConversationPrecogActionsEnum.CONVERSATION_PRECOG_UPDATED,
    payload: precog,
  };
}

export function deleteConversationPrecog(
  precogId: ConversationPrecogId,
  precogType?: PrecogTypesEnum,
): AppAction {
  return {
    type: ConversationPrecogActionsEnum.CONVERSATION_PRECOG_DELETED,
    payload: {...precogId, precogType},
  };
}

const isConversationPrecogActionType = makeEnumTypeGuard(ConversationPrecogActionsEnum);

export function isConversationPrecogAction(action: AppAction) {
  return isConversationPrecogActionType(action.type);
}

/*
 * Union type.
 */

export type ConversationPrecogAction =
  | ConversationPrecogsUpdatedAction
  | ConversationPrecogDeletedAction
  | ConversationPrecogUpdatedAction;
